import React, { useState, useEffect } from "react";
import { Eye, Pencil, Trash2, Plus, Search, XCircle } from "lucide-react";
import { formattedDate } from "../../utils/dateUtils";
import Loading from "../../component/Loading/Loading";
import AlertMsg from "../../component/AlertMsg/AlertMsg";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useSelector } from "react-redux";
import { AiOutlineSortAscending } from "react-icons/ai";
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const VendorManagement = () => {
    const axiosInstance = useAxiosInstance();
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [showViewVendor, setShowViewVendor] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [vendorToEdit, setVendorToEdit] = useState(null);
    const [editedVendor, setEditedVendor] = useState({});
    const itemsPerPage = 10;
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedServiceType, setSelectedServiceType] = useState(0);
    const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
    const communityId = useSelector((state) => state.auth.communityId);
    const userId = useSelector((state) => state.auth.userId);

    const fetchVendorsByServiceType = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `/api/public/vendor/byZipCodeAndServiceType`,
                {
                    params: {
                        serviceTypeId: selectedServiceType,
                        zipCode: 0,
                    }
                }
            );
            console.log(Array.from(response.data).length);
            setData(response.data || []);
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendorsByServiceType();
    }, [selectedServiceType]);

    const handleServiceTypeIdChange = (e) => {
        const serviceTypeId = e.target.value;
        setSelectedServiceType(serviceTypeId);
    };

    const handleViewVendor = (vendor) => {
        setSelectedVendor(vendor);
        setShowViewVendor(true);
    };

    const handleCloseViewVendor = () => {
        setSelectedVendor(null);
        setShowViewVendor(false);
    };

    const openDeleteModal = (vendor) => {
        setVendorToDelete(vendor);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setVendorToDelete(null);
        setShowDeleteModal(false);
    };

    const openEditModal = (vendor) => {
        setVendorToEdit(vendor);
        setEditedVendor({
            ...vendor,
            licenseExpiryDate: vendor.licenseExpiryDate.split("T")[0],
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setVendorToEdit(null);
        setShowEditModal(false);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;

        if (name === "licenseExpiryDate") {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time part for accurate comparison

            if (selectedDate < today) {
                // Handle invalid date (e.g., show error message)
                return;
            }
        }
        setEditedVendor((prevVendor) => ({
            ...prevVendor,
            [name]: value,
        }));
    };

    const handlePhoneChange = (value) => {
        setEditedVendor((prevVendor) => ({
            ...prevVendor,
            phone: value,
        }));
    };

    const handleServiceTypeChange = (event) => {
        const selectedTypeId = event.target.value;
        setEditedVendor({
            ...editedVendor,
            serviceTypeId: selectedTypeId,
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editedVendor.phone || !editedVendor.serviceTypeId) {
            setAlert({
                visible: true,
                message: "Please fill out all required fields.",
                type: "error",
            });
            return;
        }

        const updatedVendor = {
            ...editedVendor,
            serviceTypeId: editedVendor.serviceTypeId,
        };

        handleEdit(vendorToEdit.vendorId, updatedVendor);
    };

    const handleEdit = async (vendorId, updatedVendor) => {
        setShowEditModal(false);
        try {
            setLoading(true);
            await axiosInstance.put(`/api/public/vendor/update/${vendorId}`, updatedVendor);
            fetchVendorsByServiceType();
            setSelectedServiceType(0);
            setAlert({
                visible: true,
                message: "Vendor updated successfully!",
                type: "success",
            });
        } catch (error) {
            setAlert({
                visible: true,
                message: "Error updating vendor: Please try again.",
                type: "error",
            });
            console.error("Error updating vendor:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (status) => {
        setEditedVendor({ ...editedVendor, activeStatus: status });
    };

    const [businessTypes, setBusinessTypes] = useState([]);

    useEffect(() => {
        axiosInstance
            .get(`/api/public/servicetype/getAll`)
            .then((response) => {
                setBusinessTypes(response.data);
                console.log(response.data);
            })
            .catch((error) => {
                console.error("Error fetching business types:", error);
            });
    }, []);

    const handleDelete = async (vendorId) => {
        setShowDeleteModal(false);
        try {
            setLoading(true);
            await axiosInstance.delete(`/api/public/vendor/delete/${vendorId}`);
            fetchVendorsByServiceType();
            setAlert({
                visible: true,
                message: "Vendor deleted successfully!",
                type: "success",
            });
        } catch (error) {
            setAlert({
                visible: true,
                message: "Error deleting vendor: Please try again.",
                type: "error",
            });
            console.error("Error deleting vendor:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data.filter(
        (vendor) =>
            vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.zipCode.toString().includes(searchTerm.toLowerCase())
    );

    const [sortField, setSortField] = React.useState('licenseExpiryDate');
    const [sortOrder, setSortOrder] = React.useState('none');

    const handleSort = (field) => {
        const newSortOrder = (field === sortField && sortOrder === 'asc') ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newSortOrder);
    };

    const sortedData = React.useMemo(() => {
        return [...filteredData].sort((a, b) => {
            if (sortField === 'name') {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                return sortOrder === 'asc'
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            }
            const dateA = new Date(a.licenseExpiryDate);
            const dateB = new Date(b.licenseExpiryDate);
            return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }, [filteredData, sortField, sortOrder]);

    const getPaginatedData = () => {
        const startIndex = currentPage * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    };

    const paginatedData = getPaginatedData();

    const pageCount = Math.ceil(filteredData.length / itemsPerPage);

    // View modal footer with close button
    const viewModalFooter = (
        <div className="flex justify-end">
            <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={handleCloseViewVendor}
            >
                Close
            </button>
        </div>
    );

    // Edit modal footer with action buttons
    const editModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeEditModal}
            >
                Cancel
            </button>
            <button
                type="button"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={handleEditSubmit}
                disabled={
                    !editedVendor.name ||
                    !editedVendor.licenseExpiryDate ||
                    !editedVendor.licenceNumber ||
                    !editedVendor.serviceTypeId ||
                    !editedVendor.emailId ||
                    !editedVendor.phone ||
                    editedVendor.activeStatus === null ||
                    editedVendor.name.length < 2 ||
                    editedVendor.licenceNumber.length < 5 ||
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(editedVendor.emailId) ||
                    editedVendor.phone.length < 10 ||
                    new Date(editedVendor.licenseExpiryDate) < new Date()
                }
            >
                Save Changes
            </button>
        </div>
    );

    // Delete modal footer with action buttons
    const deleteModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeDeleteModal}
            >
                Cancel
            </button>
            <button
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                onClick={() => handleDelete(vendorToDelete.vendorId)}
            >
                Delete
            </button>
        </div>
    );

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            {loading && <Loading />}
            {!loading && (
                <div className="max-w-7xl mx-auto">
                    {alert.visible && (
                        <AlertMsg
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlert({ ...alert, visible: false })}
                        />
                    )}
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Vendor Management</h1>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                        <div className="flex flex-wrap md:flex-nowrap justify-between items-center p-4">
                            <div className="relative w-full md:w-80 mb-4 md:mb-0">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Search Vendor..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="w-full md:w-auto flex gap-2">
                                <select
                                    id="serviceTypeFilter"
                                    value={selectedServiceType}
                                    onChange={handleServiceTypeIdChange}
                                    className="px-4 w-full py-[11px] border border-gray-300 rounded-md text-sm outline-none cursor-pointer transition-colors hover:border-gray-500 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Service Types</option>
                                    {businessTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>

                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-400">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Vendor name <button
                                            onClick={() => handleSort('name')}
                                        >
                                            <AiOutlineSortAscending className="inline ml-1" />
                                        </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">License Expiry date</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Licence Number</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Service Type</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Contact Number</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Status</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                                        </tr>
                                    ) : (
                                        paginatedData.map((vendor) => (
                                            <tr key={vendor.vendorId} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vendor.name}</td>
                                                <td
                                                    className={`px-6 py-4 whitespace-nowrap text-sm ${new Date(vendor.licenseExpiryDate) - new Date() <= 10 * 24 * 60 * 60 * 1000 &&
                                                        new Date(vendor.licenseExpiryDate) - new Date() > 0
                                                        ? 'text-orange-500'
                                                        : new Date(vendor.licenseExpiryDate) - new Date() <= 0
                                                            ? 'text-red-500'
                                                            : 'text-gray-900'
                                                        }`}
                                                >
                                                    {new Date(vendor.licenseExpiryDate).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vendor.licenceNumber}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {businessTypes.find((type) => type.id === vendor.serviceTypeId)?.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vendor.phone}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span
                                                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${vendor.activeStatus === null
                                                            ? 'bg-gray-500 text-white'
                                                            : vendor.activeStatus
                                                                ? 'bg-green-500 text-white'
                                                                : 'bg-red-500 text-white'
                                                            }`}
                                                    >
                                                        {vendor.activeStatus === null
                                                            ? 'Request Status'
                                                            : vendor.activeStatus
                                                                ? 'Approved'
                                                                : 'Rejected'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-3">
                                                    <button
                                                        className="text-blue-500 hover:text-blue-700"
                                                        onClick={() => handleViewVendor(vendor)}
                                                        title="View Vendor"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    <button
                                                        className="text-gray-500 hover:text-gray-700"
                                                        onClick={() => openEditModal(vendor)}
                                                        title="Edit Vendor"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button
                                                        className="text-red-500 hover:text-red-700"
                                                        onClick={() => openDeleteModal(vendor)}
                                                        title="Delete Vendor"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                                Page {currentPage + 1} of {pageCount}
                            </div>
                            <div className="flex-1 flex justify-end">
                                <Pagination
                                    currentPage={currentPage}
                                    pageCount={pageCount}
                                    setCurrentPage={setCurrentPage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showViewVendor}
                onClose={handleCloseViewVendor}
                title="View Vendor Details"
                size="lg"
                footer={viewModalFooter}
            >
                {selectedVendor && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                                type="text"
                                value={selectedVendor.name}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">License Expiry Date</label>
                            <input
                                type="text"
                                value={formattedDate(selectedVendor.licenseExpiryDate)}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                            <input
                                type="text"
                                value={selectedVendor.licenceNumber}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                            <input
                                type="text"
                                value={
                                    businessTypes.find(
                                        (client) => client.id === selectedVendor.serviceTypeId
                                    )?.name
                                }
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                type="text"
                                maxLength={100}
                                value={selectedVendor.emailId}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                            <input
                                type="text"
                                maxLength={10}
                                value={selectedVendor.phone}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                            <input
                                type="text"
                                value={selectedVendor.activeStatus ? "Active" : "Inactive"}
                                readOnly
                                className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                            />
                        </div>
                    </div>
                )}
            </GlassmorphicModal>

            {/* Edit Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showEditModal}
                onClose={closeEditModal}
                title="Edit Vendor"
                size="lg"
                footer={editModalFooter}
            >
                {vendorToEdit && (
                    <div className="flex flex-wrap gap-6">
                        {/* Name Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={editedVendor.name}
                                onChange={handleEditChange}
                                required
                                minLength={2}
                                maxLength={50}
                                className={`w-full rounded-md border ${!editedVendor.name || editedVendor.name.length < 2 ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {!editedVendor.name && (
                                <p className="text-red-500 text-xs mt-1">Name is required</p>
                            )}
                            {editedVendor.name && editedVendor.name.length < 2 && (
                                <p className="text-red-500 text-xs mt-1">Name must be at least 2 characters</p>
                            )}
                        </div>

                        {/* License Expiry Date Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="licenseExpiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                                License Expiry Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                id="licenseExpiryDate"
                                name="licenseExpiryDate"
                                value={editedVendor.licenseExpiryDate}
                                onChange={handleEditChange}
                                min={new Date().toISOString().split('T')[0]}
                                required
                                className={`w-full rounded-md border ${!editedVendor.licenseExpiryDate || new Date(editedVendor.licenseExpiryDate) < new Date() ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {!editedVendor.licenseExpiryDate && (
                                <p className="text-red-500 text-xs mt-1">Expiry date is required</p>
                            )}
                            {editedVendor.licenseExpiryDate && new Date(editedVendor.licenseExpiryDate) < new Date() && (
                                <p className="text-red-500 text-xs mt-1">Expiry date cannot be in the past</p>
                            )}
                        </div>

                        {/* License Number Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="licenceNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                Licence Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="licenceNumber"
                                name="licenceNumber"
                                value={editedVendor.licenceNumber}
                                onChange={handleEditChange}
                                required
                                minLength={5}
                                maxLength={20}
                                className={`w-full rounded-md border ${!editedVendor.licenceNumber || editedVendor.licenceNumber.length < 5 ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {!editedVendor.licenceNumber && (
                                <p className="text-red-500 text-xs mt-1">License number is required</p>
                            )}
                            {editedVendor.licenceNumber && editedVendor.licenceNumber.length < 5 && (
                                <p className="text-red-500 text-xs mt-1">License number must be at least 5 characters</p>
                            )}
                        </div>

                        {/* Service Type Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="serviceTypeId" className="block text-sm font-medium text-gray-700 mb-1">
                                Service Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="editServiceType"
                                name="serviceTypeId"
                                value={editedVendor.serviceTypeId}
                                onChange={handleServiceTypeChange}
                                required
                                className={`w-full rounded-md border ${!editedVendor.serviceTypeId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            >
                                <option value="">Select Service Type</option>
                                {businessTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {!editedVendor.serviceTypeId && (
                                <p className="text-red-500 text-xs mt-1">Service type is required</p>
                            )}
                        </div>

                        {/* Email Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="emailId" className="block text-sm font-medium text-gray-700 mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                id="emailId"
                                name="emailId"
                                value={editedVendor.emailId}
                                onChange={handleEditChange}
                                required
                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                className={`w-full rounded-md border ${!editedVendor.emailId || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(editedVendor.emailId) ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {!editedVendor.emailId && (
                                <p className="text-red-500 text-xs mt-1">Email is required</p>
                            )}
                            {editedVendor.emailId && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(editedVendor.emailId) && (
                                <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                            )}
                        </div>

                        {/* Phone Number Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                Contact Number <span className="text-red-500">*</span>
                            </label>
                            <div className="phone-input-container">
                                <PhoneInput
                                    inputProps={{
                                        name: "phone",
                                        required: true,
                                        autoFocus: true,
                                        placeholder: "Enter mobile number",
                                        style: { padding: '20px 45px', width: '100%' },
                                    }}
                                    value={editedVendor.phone}
                                    onChange={handlePhoneChange}
                                    defaultCountry="US"
                                    inputClass={`w-full flex-1 border rounded-md border ${!editedVendor.phone || editedVendor.phone.length < 10 ? "border-red-500" : "border-gray-300"} px-4 py-[20px] outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500`}
                                    isValid={(value, country) => {
                                        if (value.match(/12345/)) {
                                            return 'Invalid phone number';
                                        } else if (value.match(/1234/)) {
                                            return false;
                                        } else if (value.length < 8) {
                                            return 'Phone number too short';
                                        } else {
                                            return true;
                                        }
                                    }}
                                />
                            </div>

                            {!editedVendor.phone && (
                                <p className="text-red-500 text-xs mt-1">Phone number is required</p>
                            )}
                            {editedVendor.phone && editedVendor.phone.length < 10 && (
                                <p className="text-red-500 text-xs mt-1">Please enter a valid phone number</p>
                            )}
                        </div>

                        {/* Active Status Field */}
                        <div className="w-full md:w-[calc(50%-1.5rem)]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Active Status <span className="text-red-500">*</span>
                            </label>
                            <div className="flex mb-5">
                                <div className="flex items-center mr-5">
                                    <input
                                        type="radio"
                                        id="activeTrue"
                                        name="activeStatus"
                                        value="true"
                                        checked={editedVendor.activeStatus === true}
                                        onChange={() => handleStatusChange(true)}
                                        required
                                        className="mr-2 focus:ring-2 focus:ring-blue-500"
                                    />
                                    <label htmlFor="activeTrue" className="text-sm">
                                        Approve
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        id="activeFalse"
                                        name="activeStatus"
                                        value="false"
                                        checked={editedVendor.activeStatus === false}
                                        onChange={() => handleStatusChange(false)}
                                        className="mr-2 focus:ring-2 focus:ring-blue-500"
                                    />
                                    <label htmlFor="activeFalse" className="text-sm">
                                        Reject
                                    </label>
                                </div>
                            </div>
                            {editedVendor.activeStatus === null && (
                                <p className="text-red-500 text-xs mt-1">Please select a status</p>
                            )}
                        </div>
                    </div>
                )}
            </GlassmorphicModal>

            {/* Delete Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showDeleteModal}
                onClose={closeDeleteModal}
                title="Confirm Delete"
                size="sm"
                footer={deleteModalFooter}
            >
                <p className="text-gray-700">
                    Are you sure you want to delete {vendorToDelete?.name}? This action cannot be undone.
                </p>
            </GlassmorphicModal>
        </div>
    );
};

export default VendorManagement;